/**
 * First-party behavioral telemetry sink (Growth Intelligence Engine — Phase 0 feeder).
 *
 * A tiny, typed, side-effect-safe client batcher that mirrors EVERY observability event to our
 * own backend (`POST /api/telemetry/events`) — independently of Microsoft Clarity. Clarity stays
 * a third-party recorder; this is the first-party signal the engine actually owns.
 *
 * Design rules (identical posture to clarity.ts):
 *  - Telemetry NEVER throws and NEVER blocks the UI. Every public fn is a no-op when disabled and
 *    swallows its own errors.
 *  - Best-effort delivery: batched in memory, flushed via `navigator.sendBeacon` (with a
 *    `fetch(keepalive)` fallback) on size / interval / page-hide.
 *  - Never sends PII: string prop values are scrubbed with the same `sanitizeTagValue` as Clarity.
 *  - Does NOT depend on Clarity being enabled (the fan-out in clarity.ts runs before its own gate).
 *
 * Frozen contract — two destinations chosen by whether a user (authed) cid exists:
 *  - user cid set (post-login): authed body { events: [ { cid, name, ts, props } ] } → /telemetry/events
 *  - no user cid (pre-login):   anon  body { anonCid, attribution?, events: [ { name, ts, props } ] }
 *                               → /telemetry/anon-events (PUBLIC, Phase 0.5).
 */

import { sanitizeTagValue, type ClarityTags } from './clarity';
// Aliased: this module already derives its own trailing-slash-stripped API_BASE below.
import { API_BASE as RUNTIME_API_BASE } from '../../config/runtimeConfig';
import { getAnonCid, getAttribution, captureFirstTouchAttribution } from './anon';

// ── Environment (Vite inlines these statically at build time, except apiUrl, which is
//    resolved at boot so one artifact can serve every environment) ───────────
const ENV = {
  enableFlag: import.meta.env.VITE_ENABLE_TELEMETRY,
  apiUrl: RUNTIME_API_BASE,
  mode: import.meta.env.MODE,
} as const;

/** Same-origin/api endpoints, derived from the shared API base. */
const API_BASE = ENV.apiUrl.replace(/\/$/, '');
const TELEMETRY_ENDPOINT = `${API_BASE}/telemetry/events`;
/** PUBLIC anonymous ingest (no auth) — used pre-login when no user cid is set (Phase 0.5). */
const ANON_TELEMETRY_ENDPOINT = `${API_BASE}/telemetry/anon-events`;

const MAX_BATCH = 20;
const FLUSH_INTERVAL_MS = 5000;

/**
 * Runtime opt-in for verification (E2E) only — the SAME flag the Clarity layer honors, so the
 * `vite build --mode test` E2E build can exercise the sink without a real prod env. Never honored
 * in a REAL production build (MODE==='production'), where telemetry is already first-party-on.
 */
const TELEMETRY_TEST_FLAG = '__bizcore_clarity_force__';
function runtimeTestEnabled(): boolean {
  if (ENV.mode === 'production') return false;
  try {
    return typeof window !== 'undefined' && window.localStorage.getItem(TELEMETRY_TEST_FLAG) === '1';
  } catch {
    return false;
  }
}

let cachedEnabled: boolean | undefined;

/** Whether the first-party sink should run in this environment. Cached after first evaluation. */
export function isTelemetryEnabled(): boolean {
  if (cachedEnabled !== undefined) return cachedEnabled;
  cachedEnabled = computeEnabled();
  return cachedEnabled;
}

function computeEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (ENV.enableFlag === 'false') return false; // hard kill-switch, any env
  if (ENV.mode === 'production') return true; // first-party: always on in a real prod build
  if (ENV.mode === 'test') return runtimeTestEnabled(); // E2E build: only via the runtime flag
  // development / staging / preview: off unless explicitly opted in (or the E2E flag is set).
  if (ENV.enableFlag === 'true') return true;
  return runtimeTestEnabled();
}

/** Test-only: reset the memoized enabled flag (parity with __resetClarityEnabledCache). */
export function __resetTelemetryEnabledCache(): void {
  cachedEnabled = undefined;
}

// ── Correlation id (opaque, backend-provided via /bootstrap) ──────────────────
let currentCid = '';

/** Set the opaque correlation id used as `cid` on every event. Absent/empty is fine. */
export function setSinkCorrelationId(cid: string | null | undefined): void {
  currentCid = typeof cid === 'string' ? cid : '';
}

// ── Buffer ────────────────────────────────────────────────────────────────────
interface BufferedEvent {
  name: string;
  ts: number;
  props: Record<string, string | number | boolean>;
}

const buffer: BufferedEvent[] = [];

/** Scrub prop values: redact PII in strings (sanitizeTagValue), pass numbers/booleans through. */
function sanitizeProps(props?: ClarityTags): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  if (!props) return out;
  for (const [key, value] of Object.entries(props)) {
    if (value === null || value === undefined || value === '') continue;
    out[key] = typeof value === 'string' ? sanitizeTagValue(value) : value;
  }
  return out;
}

/** Buffer one behavioral event. No-op + swallows errors when disabled. Never throws. */
export function enqueueBehavioralEvent(name: string, props?: ClarityTags): void {
  if (!isTelemetryEnabled()) return;
  try {
    buffer.push({ name, ts: Date.now(), props: sanitizeProps(props) });
    ensureScheduled();
    if (buffer.length >= MAX_BATCH) flush();
  } catch {
    /* telemetry must never break the app */
  }
}

// ── Flush / transport ─────────────────────────────────────────────────────────
function flush(): void {
  if (buffer.length === 0) return;
  if (!isTelemetryEnabled()) {
    buffer.length = 0;
    return;
  }
  const drained = buffer.splice(0, buffer.length);
  try {
    if (currentCid) {
      // Authed path (Phase 0, unchanged): cid per event → /telemetry/events.
      const events = drained.map((e) => ({ cid: currentCid, name: e.name, ts: e.ts, props: e.props }));
      transmit(TELEMETRY_ENDPOINT, JSON.stringify({ events }));
    } else {
      // Anon path (Phase 0.5): no per-event cid; top-level anonCid + first-touch attribution
      // → PUBLIC /telemetry/anon-events. Attribution is captured (idempotently) on first flush.
      captureFirstTouchAttribution();
      const events = drained.map((e) => ({ name: e.name, ts: e.ts, props: e.props }));
      const payload = { anonCid: getAnonCid(), attribution: getAttribution(), events };
      transmit(ANON_TELEMETRY_ENDPOINT, JSON.stringify(payload));
    }
  } catch {
    /* best-effort; drop on failure rather than ever throwing */
  }
}

function transmit(endpoint: string, payload: string): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'application/json' });
      if (navigator.sendBeacon(endpoint, blob)) return;
    }
  } catch {
    /* fall through to fetch */
  }
  try {
    void fetch(endpoint, {
      method: 'POST',
      body: payload,
      keepalive: true,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    }).catch(() => {
      /* swallow — best-effort */
    });
  } catch {
    /* swallow — telemetry must never throw */
  }
}

// ── Scheduling (lazily installed on first enqueue) ────────────────────────────
let scheduled = false;
function ensureScheduled(): void {
  if (scheduled) return;
  scheduled = true;
  try {
    if (typeof window === 'undefined') return;
    window.setInterval(flush, FLUSH_INTERVAL_MS);
    window.addEventListener('pagehide', flush);
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush();
      });
    }
  } catch {
    /* scheduling is best-effort; size-based flush still works */
  }
}
